import { createSlice } from '@reduxjs/toolkit';
import { portalApi } from '../api/portalApi';

// Re-export RTK Query hooks for direct component use
export {
  useGetDataQuery,
  usePostDataMutation,
  usePutDataMutation,
  usePatchDataMutation,
  useDeleteDataMutation,
} from '../api/portalApi';

/**
 * Backward-compatible thunk wrappers.
 * These delegate to the RTK Query generic endpoints so existing call-sites
 * (dispatch(fetchData({url, params}))) continue to work without modification.
 */
export const fetchData = ({ url, params = {}, contract }) => (dispatch) => {
  // forceRefetch: true preserves the old always-fresh axios behavior –
  // components that call fetchData() always expect an up-to-date response.
  return dispatch(
    portalApi.endpoints.getData.initiate({ url, params, contract }, { forceRefetch: true })
  );
};

export const postData = ({ url, data, contract }) => (dispatch) => {
  return dispatch(portalApi.endpoints.postData.initiate({ url, data, contract }));
};

export const patchData = ({
  url,
  data,
  baselineData = null,
  allowFullPayload = false,
  contract,
}) => (dispatch) => {
  return dispatch(
    portalApi.endpoints.patchData.initiate({
      url,
      data,
      baselineData,
      allowFullPayload,
      contract,
    })
  );
};

export const deleteData = ({ url, data, deleteId }) => (dispatch) => {
  return dispatch(
    portalApi.endpoints.deleteData.initiate({
      url,
      data: data ?? deleteId,
      deleteId,
    })
  );
};

// Keep PUT distinct from PATCH for legacy callers that depend on full update semantics.
export const putData = ({ url, data }) => (dispatch) => {
  return dispatch(
    portalApi.endpoints.putData.initiate({
      url,
      data,
    })
  );
};

// Initial state for the slice
const initialState = {
  data: {},
  loading: false,
  error: null,
};

// Create the slice
const apiCallSlice = createSlice({
  name: 'apiCall',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // ── RTK Query: getData ──────────────────────────────────────────────
      .addMatcher(
        portalApi.endpoints.getData.matchPending,
        (state) => { state.loading = true; state.error = null; }
      )
      .addMatcher(
        portalApi.endpoints.getData.matchFulfilled,
        (state, action) => { state.loading = false; state.data = action.payload; }
      )
      .addMatcher(
        portalApi.endpoints.getData.matchRejected,
        (state, action) => { state.loading = false; state.error = action.error?.message ?? null; }
      )
      // ── RTK Query: postData ─────────────────────────────────────────────
      .addMatcher(
        portalApi.endpoints.postData.matchPending,
        (state) => { state.loading = true; state.error = null; }
      )
      .addMatcher(
        portalApi.endpoints.postData.matchFulfilled,
        (state, action) => { state.loading = false; state.data = action.payload; }
      )
      .addMatcher(
        portalApi.endpoints.postData.matchRejected,
        (state, action) => { state.loading = false; state.error = action.error?.message ?? null; }
      )
      // ── RTK Query: putData ──────────────────────────────────────────────
      .addMatcher(
        portalApi.endpoints.putData.matchPending,
        (state) => { state.loading = true; state.error = null; }
      )
      .addMatcher(
        portalApi.endpoints.putData.matchFulfilled,
        (state, action) => { state.loading = false; state.data = action.payload; }
      )
      .addMatcher(
        portalApi.endpoints.putData.matchRejected,
        (state, action) => { state.loading = false; state.error = action.error?.message ?? null; }
      )
      // ── RTK Query: patchData ────────────────────────────────────────────
      .addMatcher(
        portalApi.endpoints.patchData.matchPending,
        (state) => { state.loading = true; state.error = null; }
      )
      .addMatcher(
        portalApi.endpoints.patchData.matchFulfilled,
        (state, action) => { state.loading = false; state.data = action.payload; }
      )
      .addMatcher(
        portalApi.endpoints.patchData.matchRejected,
        (state, action) => { state.loading = false; state.error = action.error?.message ?? null; }
      )
      // ── RTK Query: deleteData ───────────────────────────────────────────
      .addMatcher(
        portalApi.endpoints.deleteData.matchPending,
        (state) => { state.loading = true; state.error = null; }
      )
      .addMatcher(
        portalApi.endpoints.deleteData.matchFulfilled,
        (state, action) => { state.loading = false; state.data = action.payload; }
      )
      .addMatcher(
        portalApi.endpoints.deleteData.matchRejected,
        (state, action) => { state.loading = false; state.error = action.error?.message ?? null; }
      );
  },
});

export default apiCallSlice.reducer;